import React, { FunctionComponent, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { camelCase } from 'lodash';

import PopupDialog from '../../../shared/PopupDialog';

import RunDetailDialog from './RunDetailDialog';
import RunHistoryGrid from './RunHistoryGrid';
import RunHistoryPagination from './RunHistoryPagination';
import {
  DEFAULT_PAGE_SIZE,
  IRunHistoryRow,
  RunHistoryScope,
} from './runHistoryApi';

const TITLE = 'Inter-Model Run History';

interface IImdlRunHistoryDialogProps {
  open: boolean;
  close: () => void;
  modelName?: string;
  instanceName?: string;
  modelInstanceId?: number;
  scope?: RunHistoryScope;
  clientName?: string;
  /** Client scope: preselects the Target Model filter. */
  initialTargetModel?: string;
}

const ImdlRunHistoryDialog: FunctionComponent<IImdlRunHistoryDialogProps> = ({
  open,
  close,
  modelName,
  instanceName,
  modelInstanceId,
  scope,
  clientName,
  initialTargetModel,
}) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [selectedRow, setSelectedRow] = useState<IRunHistoryRow | null>(null);
  const isClient = scope?.kind === 'client';
  const hasScope = scope !== undefined || modelInstanceId !== undefined;
  // Computed once: a new object each render would re-apply the preset.
  const [initialFilterModel] = useState(() =>
    initialTargetModel
      ? { targetModel: { value: initialTargetModel } }
      : undefined
  );
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  // The total can shrink under a later page (a run was removed); without this
  // the pager shows the last page while the query stays on the old one.
  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  return (
    <PopupDialog
      open={open}
      close={close}
      title={TITLE}
      maxWidth={false}
      PaperProps={{
        style: {
          width: '100vw',
          height: '95vh',
          maxWidth: '100vw',
          maxHeight: '95vh',
          margin: 0,
        },
      }}
      // The grid is flex: 1, so the content box must fill the paper.
      contentSx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
      closeButtonLabel="Close"
      primaryButtons={[]}
      actionsContent={
        hasScope && (
          <RunHistoryPagination
            page={page}
            pageCount={pageCount}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPage(1);
              setPageSize(size);
            }}
          />
        )
      }
      // PopupDialog's default labelledby points at the title text, not its id.
      aria-labelledby={`${camelCase(TITLE)}-id`}
    >
      <Box
        sx={{
          mb: 2,
          pb: 2,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          gap: 4,
        }}
      >
        {isClient ? (
          <Box>
            <Typography variant="caption" color="text.secondary">
              Client
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {clientName}
            </Typography>
          </Box>
        ) : (
          <>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Model
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {modelName}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Model Instance
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {instanceName}
              </Typography>
            </Box>
          </>
        )}
      </Box>
      {hasScope && (
        <RunHistoryGrid
          scope={scope}
          modelInstanceId={modelInstanceId}
          initialFilterModel={initialFilterModel}
          onViewRunDetail={isClient ? setSelectedRow : undefined}
          page={page}
          pageSize={pageSize}
          onTotal={setTotal}
          onPageReset={() => setPage(1)}
        />
      )}
      {scope?.kind === 'client' && (
        <RunDetailDialog
          open={selectedRow !== null}
          close={() => setSelectedRow(null)}
          clientId={scope.clientId}
          rowId={selectedRow?.rowId ?? null}
        />
      )}
    </PopupDialog>
  );
};

export default ImdlRunHistoryDialog;
